import { Table } from 'antd';
import React, { useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DAYTIME_FORMAT } from '../../constants';
import useColumnConfig from '../../hooks/useColumnConfig';
import ColumnConfigDrawer from '../ColumnConfigDrawer';
import DriverAvatar from '../DriverAvatar';

const FIXED_KEYS = ['name', 'action'];

export default function RidersTable({
  withAction,
  dataSource,
  selectedRowKeys,
  onSelectChange,
  configRef,
  onPageSizeChange,
  ...props
}) {
  const { t: tc } = useTranslation('common');
  const allColumns = useMemo(() => {
    const cols = [
      {
        title: tc('name'),
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        className: 'border-right',
        render: (name, record) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <DriverAvatar name={name} photoKey={record?.photoFile?.key} size={24} />
            {name}
          </span>
        ),
      },
      {
        title: tc('phoneNumber'),
        dataIndex: 'phone',
        key: 'phone',
        width: 200,
        render: (value) => {
          return value?.includes('+') && value[0] !== '0' ? value : value ? `+${value}` : '-';
        },
      },
      {
        title: tc('skills'),
        dataIndex: 'skills',
        key: 'skills',
        render: (skills) => {
          return skills?.length > 0 ? (
            <div className="skills">
              {skills.map((skill) => {
                return (
                  <span key={skill} className="skill">
                    {skill}
                  </span>
                );
              })}
            </div>
          ) : (
            ''
          );
        },
      },
      {
        title: tc('updatedAt'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 250,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
      {
        title: tc('createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 200,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
    ];

    if (withAction) {
      cols.push({
        key: 'action',
        width: 120,
        className: 'border-left',
        fixed: 'right',
        render(record) {
          return withAction(record);
        },
      });
    }

    return cols;
  }, [withAction]);

  const columnConfig = useColumnConfig('drivers-columns', allColumns, FIXED_KEYS);

  // Expose openDrawer to parent via ref
  if (configRef) {
    configRef.current = { openDrawer: columnConfig.openDrawer, pageSize: columnConfig.pageSize };
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="table-job-orders table-common">
      <Table
        columns={columnConfig.visibleColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-dark' : 'table-row-light')}
        {...props}
      />
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={(size) => {
          columnConfig.setPageSize(size);
          // Server-paginated page — tell it to refetch with the new take.
          onPageSizeChange?.(size);
        }}
      />
    </div>
  );
}
